<script setup>
import { computed, toRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDialogA11y } from 'dashboard/composables/useDialogA11y';
import desktopImage from 'dashboard/assets/real-estate/broker-tax-document-feedback/image_upload_nota.svg';
import mobileImage from 'dashboard/assets/real-estate/broker-tax-document-feedback/feedback-upload-nota.svg';

const props = defineProps({
  quantity: { type: Number, default: 1 },
  show: { type: Boolean, default: false },
});
const emit = defineEmits(['close']);
const { t } = useI18n();
const titleId = 'real-estate-finance-boleto-feedback-title';
const isSingleBoleto = computed(() => props.quantity === 1);
const feedbackTitle = computed(() =>
  isSingleBoleto.value
    ? t('REAL_ESTATE_FINANCE.BOLETO.FEEDBACK.TITLE_SINGLE')
    : t('REAL_ESTATE_FINANCE.BOLETO.FEEDBACK.TITLE_MULTIPLE')
);
const feedbackMessage = computed(() =>
  isSingleBoleto.value
    ? t('REAL_ESTATE_FINANCE.BOLETO.FEEDBACK.MESSAGE_SINGLE')
    : t('REAL_ESTATE_FINANCE.BOLETO.FEEDBACK.MESSAGE_MULTIPLE')
);
const { dialogRef } = useDialogA11y({
  isOpen: toRef(props, 'show'),
  onClose: () => emit('close'),
});
</script>

<template>
  <div>
    <div
      v-if="show"
      class="fixed inset-0 z-lais-modal grid place-items-center overflow-y-auto bg-slate-950/40 p-0 min-[751px]:p-4"
      data-test="finance-boleto-feedback-dialog"
      @click.self="emit('close')"
    >
      <section
        ref="dialogRef"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="titleId"
        tabindex="-1"
        class="relative flex min-h-dvh w-full max-h-[100dvh] flex-col overflow-y-auto bg-white p-6 pb-28 min-[751px]:min-h-0 min-[751px]:max-h-[calc(100dvh-2rem)] min-[751px]:max-w-[1036px] min-[751px]:rounded-[10px] min-[751px]:p-8"
      >
        <button
          type="button"
          class="absolute right-2 top-2 grid size-9 place-items-center rounded-full text-lais-text-muted hover:bg-lais-primary-50 focus-visible:outline-none focus-visible:shadow-lais-focus"
          :aria-label="t('REAL_ESTATE_FINANCE.ACTIONS.CLOSE')"
          data-test="finance-boleto-feedback-close"
          @click="emit('close')"
        >
          <span class="i-lucide-x size-4" aria-hidden="true" />
        </button>

        <div
          class="grid flex-1 gap-8 pt-[60px] min-[751px]:grid-cols-[482px_minmax(0,1fr)] min-[751px]:items-stretch min-[751px]:gap-[30px] min-[751px]:pt-0"
        >
          <div class="flex items-start justify-center">
            <img
              :src="desktopImage"
              :alt="t('REAL_ESTATE_FINANCE.BOLETO.FEEDBACK.DESKTOP_ALT')"
              class="hidden w-full max-w-[482px] object-contain min-[751px]:block"
              data-test="finance-boleto-feedback-desktop-image"
            />
            <img
              :src="mobileImage"
              :alt="t('REAL_ESTATE_FINANCE.BOLETO.FEEDBACK.MOBILE_ALT')"
              class="block w-full max-w-[312px] object-contain min-[751px]:hidden"
              data-test="finance-boleto-feedback-mobile-image"
            />
          </div>

          <div
            class="flex flex-1 flex-col min-[751px]:mt-[45px] min-[751px]:justify-between"
          >
            <div class="space-y-3 text-left">
              <h2
                :id="titleId"
                class="font-laisDisplay text-2xl font-bold leading-[30px] text-[#464F53]"
              >
                {{ feedbackTitle }}
              </h2>
              <p class="text-base leading-6 text-[#464F53]">
                {{ feedbackMessage }}
              </p>
            </div>
            <button
              type="button"
              class="fixed inset-x-6 bottom-6 z-lais-raised flex min-h-12 items-center justify-center rounded-[10px] border border-[#01988C] bg-[#01988C] px-6 py-3 text-base font-bold text-white hover:bg-[#01655D] focus-visible:outline-none focus-visible:shadow-lais-focus min-[751px]:static min-[751px]:ml-auto min-[751px]:mt-8 min-[751px]:w-auto"
              data-test="finance-boleto-feedback-return"
              @click="emit('close')"
            >
              {{ t('REAL_ESTATE_FINANCE.ACTIONS.CLOSE') }}
            </button>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>
